import { ListItemButton } from '@mui/material';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { LegStatus } from '@api/Trinium/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { batchLegLabel, StatusActionProps } from '@components/DataGrid/Dispatch/types';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

/**
 * Run an Undo Leg Status on the selected dispatch leg
 */
export default function UndoStatusAction({ leg, nextLegStatus, onError, selectedRows }: StatusActionProps & { nextLegStatus?: LegStatus }) {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [putLegs] = usePutLegsMutation();
  const ensuingLegStatus = getEnumValueByKey(LegStatus, nextLegStatus || 'UN') as LegStatus;

  const isUndoDisabled = (targetLeg = leg, targetNextLegStatus = LegStatus.UN): {
    disabledStatus: boolean;
    disabledMessage: string | undefined;
  } => {
    const legStatus = getEnumValueByKey(LegStatus, targetLeg.legStatus) as LegStatus;

    if ([LegStatus.NA, LegStatus.OS].includes(legStatus) && targetLeg.drvId == undefined)
      return {
        disabledStatus: true,
        disabledMessage: 'No Driver Assigned'
      };

    if (legStatus === LegStatus.DL && targetLeg.isInvoiced)
      return {
        disabledStatus: true,
        disabledMessage: 'Undo action unavailable (invoiced)'
      };

    if (
      legStatus === LegStatus.DL &&
      targetNextLegStatus !== LegStatus.UN &&
      targetNextLegStatus !== LegStatus.AV &&
      targetNextLegStatus !== LegStatus.OS
    )
      return {
        disabledStatus: true,
        disabledMessage: 'Subsequent leg must be Available or On Site'
      };
    return { disabledStatus: false, disabledMessage: undefined };
  };

  const candidateRows = selectedRows?.some((selectedRow) => selectedRow.legId === leg.legId) ? selectedRows : [leg];
  const targetRows = candidateRows.filter((targetLeg) => {
    const nextStatus = targetLeg.legId === leg.legId ? ensuingLegStatus : LegStatus.UN;
    return !isUndoDisabled(targetLeg, nextStatus).disabledStatus;
  });
  /** The clicked leg gates the action so the row the dispatcher is looking at is never silently skipped */
  const { disabledStatus, disabledMessage } = isUndoDisabled(leg, ensuingLegStatus);

  const handleBtnClick = () => {
    void putLegs({
      terminalNumber,
      actionType: {
        action: 'undo',
        data: {
          legIds: targetRows.map((targetRow) => targetRow.legId)
        }
      }
    })
      .unwrap()
      .then((response) => {
        response.errors.forEach((error) => {
          if (onError) onError(error.errorMessage);
          else appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
        });
      })
      .catch((error: unknown) => {
        const errorMessage = getApiErrorMessage(error);
        if (onError) onError(errorMessage);
        else appDispatch(appendErrorAnnouncement({ message: errorMessage }));
      });
  };

  return (
    <DisabledTooltip message={disabledMessage}>
      <ListItemButton
        onClick={handleBtnClick}
        disabled={disabledStatus}>
        {batchLegLabel('Undo', targetRows.length)}
      </ListItemButton>
    </DisabledTooltip>
  );
}
